import React, { useId, useState } from "react";

import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { getLanguageFromLocalStorage, normalizeDiacritics, useAppType, useTranslation } from "utils";

import { ACQUISITION_CROPS, ACQUISITION_CROP_BY_ID, ACQUISITION_MAX_CROPS, ACQUISITION_MIN_CROPS, AcquisitionCrop, LOCALE_DICT, cropLabelKey } from "consts";

import { Button, Drawer, Icon, InputSearch, LazyLoadImage, Typography } from "components";

type RotationStepProps = {
  cropIds: string[];
  onCropIdsChange: (cropIds: string[]) => void;
};

type SortableRotationRowProps = {
  sortableId: string;
  cropId: string;
  yearNumber: number;
  onRemove: (yearIndex: number) => void;
};

const isCoarsePointer = () => window.matchMedia("(pointer: coarse)").matches;

const CropThumb = ({ crop }: { crop: AcquisitionCrop | undefined }) => (
  <span className="soilsight-acquisition-rotation__crop-thumb" aria-hidden="true">
    {crop?.imageSrc ? (
      <LazyLoadImage
        src={crop.imageSrc}
        alt=""
        className="soilsight-acquisition-rotation__crop-img"
        wrapperClassName="soilsight-acquisition-rotation__crop-img-wrap"
        visibleByDefault
      />
    ) : (
      <Icon iconName="tabler/plant" size="s" />
    )}
  </span>
);

const SortableRotationRow = ({ sortableId, cropId, yearNumber, onRemove }: SortableRotationRowProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const crop = ACQUISITION_CROP_BY_ID[cropId];

  return (
    <li
      ref={setNodeRef}
      className="soilsight-acquisition-rotation__year-row"
      style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined, opacity: isDragging ? 0.7 : 1 }}
      data-testid={`soilsight-rotation-year-${yearNumber}`}
    >
      <button
        type="button"
        className="soilsight-acquisition-rotation__drag"
        aria-label={t("preLogin.simulation.rotation.reorderLabel")}
        {...listeners}
        {...attributes}
      >
        <Icon iconName="tabler/grip-horizontal" size="s" />
      </button>
      <Typography variant="paragraph-m" color="grey-600" className="soilsight-acquisition-rotation__year-label">
        {t("preLogin.simulation.rotation.yearLabel", { year: yearNumber })}
      </Typography>
      <div className="soilsight-acquisition-rotation__crop-chip">
        <CropThumb crop={crop} />
        <Typography variant="paragraph-m" className="soilsight-acquisition-rotation__crop-name">
          {crop ? t(cropLabelKey(crop)) : cropId}
        </Typography>
        <button
          type="button"
          className="soilsight-acquisition-rotation__icon-btn soilsight-acquisition-rotation__icon-btn--remove"
          aria-label={t("preLogin.simulation.rotation.removeLabel")}
          data-testid={`soilsight-rotation-remove-${yearNumber}`}
          onClick={() => onRemove(yearNumber - 1)}
        >
          <Icon iconName="tabler/x" size="xs" />
        </button>
      </div>
    </li>
  );
};

export const isRotationStepComplete = (cropIds: string[]): boolean => cropIds.length >= ACQUISITION_MIN_CROPS && cropIds.length <= ACQUISITION_MAX_CROPS;

export const RotationStep = ({ cropIds, onCropIdsChange }: RotationStepProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const sortLocale = LOCALE_DICT[getLanguageFromLocalStorage()] ?? LOCALE_DICT.de;
  const { mobile: isPhone } = useAppType();
  const [searchQuery, setSearchQuery] = useState("");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const triggerId = useId();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Index-based ids change on reorder, so React remounts the moved rows and keyboard reorder can lose focus. Stable row ids fix this if it matters.
  const sortableIds = cropIds.map((cropId, yearIndex) => `${yearIndex}-${cropId}`);

  const cropName = (crop: AcquisitionCrop) => t(cropLabelKey(crop));
  const searchVariants = normalizeDiacritics(searchQuery.trim());
  const matchesSearch = (crop: AcquisitionCrop) => normalizeDiacritics(cropName(crop)).some((name) => searchVariants.some((term) => name.includes(term)));

  const cropsByName = [...ACQUISITION_CROPS].sort((cropA, cropB) => cropName(cropA).localeCompare(cropName(cropB), sortLocale));
  const filteredCrops = cropsByName.filter(matchesSearch);

  const closePicker = () => {
    setIsPickerOpen(false);
    setSearchQuery("");
    document.getElementById(triggerId)?.focus();
  };

  const handleAddCrop = (cropId: string) => {
    if (cropIds.length >= ACQUISITION_MAX_CROPS) return;
    const nextCropIds = [...cropIds, cropId];
    onCropIdsChange(nextCropIds);
    if (isPhone && nextCropIds.length >= ACQUISITION_MAX_CROPS) closePicker();
  };

  const handleRemove = (yearIndex: number) => {
    onCropIdsChange(cropIds.filter((_, cropIndex) => cropIndex !== yearIndex));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = sortableIds.indexOf(String(active.id));
    const toIndex = sortableIds.indexOf(String(over.id));
    if (fromIndex === -1 || toIndex === -1) return;
    onCropIdsChange(arrayMove(cropIds, fromIndex, toIndex));
  };

  const renderCropOption = (crop: AcquisitionCrop) => (
    <li key={crop.id}>
      <button
        type="button"
        className="soilsight-acquisition-rotation__crop-option"
        data-testid={`soilsight-rotation-add-${crop.nameKey}`}
        disabled={cropIds.length >= ACQUISITION_MAX_CROPS}
        onClick={() => handleAddCrop(crop.id)}
      >
        <CropThumb crop={crop} />
        <Typography variant="paragraph-m" className="soilsight-acquisition-rotation__crop-name">
          {cropName(crop)}
        </Typography>
        {isPhone ? null : (
          <span className="soilsight-acquisition-rotation__icon-btn" aria-hidden="true">
            <Icon iconName="tabler/chevron-right" size="xs" />
          </span>
        )}
      </button>
    </li>
  );

  return (
    <div className="soilsight-acquisition-rotation" data-testid="soilsight-step-rotation">
      <div className="soilsight-acquisition-rotation__intro">
        <Typography variant="h2" className="soilsight-acquisition-rotation__title">
          {t("preLogin.simulation.rotation.title")}
        </Typography>
        <Typography variant="paragraph-m" className="soilsight-acquisition-rotation__subtitle">
          {t("preLogin.simulation.rotation.subtitle")}
        </Typography>
      </div>

      <div className="soilsight-acquisition-rotation__columns">
        {isPhone ? (
          <>
            <Button
              id={triggerId}
              className="soilsight-acquisition-rotation__picker-trigger"
              dataTestId="soilsight-rotation-picker"
              mixpanelText="Open crop picker"
              onClick={() => setIsPickerOpen(true)}
            >
              <Icon iconName="tabler/search" size="s" />
              <Typography variant="paragraph-m" className="soilsight-acquisition-rotation__picker-trigger-label">
                {t("preLogin.simulation.rotation.pickerPlaceholder")}
              </Typography>
              <Icon iconName="tabler/chevron-down" size="s" />
            </Button>
            <Drawer
              visible={isPickerOpen}
              className="soilsight-acquisition-rotation__picker-drawer"
              title={t("preLogin.simulation.rotation.pickerPlaceholder")}
              dataTestId="soilsight-rotation-drawer"
              onClose={closePicker}
              footer={
                <div className="soilsight-acquisition-rotation__picker-footer">
                  <Typography
                    variant="paragraph-m"
                    color="grey-600"
                    className="soilsight-acquisition-rotation__added-count"
                    data-testid="soilsight-rotation-added-count"
                  >
                    {t("preLogin.simulation.rotation.addedCount", { count: cropIds.length, max: ACQUISITION_MAX_CROPS })}
                  </Typography>
                  <Button type="primary" className="soilsight-acquisition-rotation__done" dataTestId="soilsight-rotation-done" onClick={closePicker}>
                    {t("preLogin.simulation.rotation.done")}
                  </Button>
                </div>
              }
            >
              <InputSearch
                className="soilsight-acquisition-rotation__search soilsight-acquisition-rotation__picker-search"
                value={searchQuery}
                placeholder={t("preLogin.simulation.rotation.searchPlaceholder")}
                dataTestId="soilsight-rotation-search"
                autoFocus={!isCoarsePointer()}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {filteredCrops.length === 0 ? (
                <Typography
                  variant="paragraph-m"
                  color="grey-600"
                  className="soilsight-acquisition-rotation__picker-empty"
                  data-testid="soilsight-rotation-no-results"
                >
                  {t("preLogin.simulation.rotation.noResults")}
                </Typography>
              ) : (
                <ul className="soilsight-acquisition-rotation__picker-options">{filteredCrops.map(renderCropOption)}</ul>
              )}
            </Drawer>
          </>
        ) : (
          <section className="soilsight-acquisition-rotation__picker" aria-label={t("preLogin.simulation.rotation.searchLabel")}>
            <InputSearch
              className="soilsight-acquisition-rotation__search"
              value={searchQuery}
              placeholder={t("preLogin.simulation.rotation.searchPlaceholder")}
              dataTestId="soilsight-rotation-search"
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            <ul className="soilsight-acquisition-rotation__crop-list">{filteredCrops.map(renderCropOption)}</ul>
          </section>
        )}

        <section className="soilsight-acquisition-rotation__baseline" aria-label={t("preLogin.simulation.rotation.baselineTitle")}>
          <header className="soilsight-acquisition-rotation__baseline-header">
            <div className="soilsight-acquisition-rotation__baseline-copy">
              <Typography variant="paragraph-m" bold className="soilsight-acquisition-rotation__baseline-title">
                {t("preLogin.simulation.rotation.baselineTitle")}
              </Typography>
            </div>
            <span className="soilsight-acquisition-rotation__baseline-chevron" aria-hidden="true">
              <Icon iconName="tabler/chevron-right" size="m" />
            </span>
          </header>

          {cropIds.length === 0 ? (
            <div className="soilsight-acquisition-rotation__empty" data-testid="soilsight-rotation-empty">
              <div className="soilsight-acquisition-rotation__empty-content">
                <span className="soilsight-acquisition-rotation__empty-icon" aria-hidden="true">
                  <Icon iconName="klim/crop-footprint" size={48} />
                </span>
                <div className="soilsight-acquisition-rotation__empty-copy">
                  <Typography variant="h5" color="grey-600" className="soilsight-acquisition-rotation__empty-title">
                    {t("preLogin.simulation.rotation.emptyTitle")}
                  </Typography>
                  <Typography variant="paragraph-m" color="grey-600" className="soilsight-acquisition-rotation__empty-body">
                    {t("preLogin.simulation.rotation.emptyBody")}
                  </Typography>
                </div>
              </div>
              <span className="soilsight-acquisition-rotation__empty-spacer" aria-hidden="true" />
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <ol className="soilsight-acquisition-rotation__years">
                  {cropIds.map((cropId, yearIndex) => (
                    <SortableRotationRow
                      key={sortableIds[yearIndex]}
                      sortableId={sortableIds[yearIndex]}
                      cropId={cropId}
                      yearNumber={yearIndex + 1}
                      onRemove={handleRemove}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          )}
        </section>
      </div>
    </div>
  );
};
